import React, {FC, useCallback, useEffect, useRef, useState} from 'react'
import {useLocation} from 'react-router-dom'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import trLocale from '@fullcalendar/core/locales/tr'
import {DatesSetArg, DateSelectArg, EventClickArg, EventInput} from '@fullcalendar/core'
import {ToastContainer} from 'react-toastify'
import {PageTitle} from '../../../../_metronic/layout/core'
import {ConfirmModal} from '../../../../shared/components/ConfirmModal'
import {showErrorToast, showSuccessToast} from '../../../../shared/components/ToastUtils'
import {apiErrorMessage} from '../../public/PublicModels'
import {HR_INTERVIEW_PATH} from '../interview/InterviewModel'
import {
  createCalendarEvent,
  deleteCalendarEvent,
  loadOccurrences,
  setTaskCompleted,
  updateCalendarEvent,
} from './Api'
import {ICalendarEventRequest, ICalendarOccurrence, toDateOnly} from './CalendarModel'
import {EventDetailModal} from './EventDetailModal'
import {EventFormModal} from './EventFormModal'
import {GoogleConnectionBar} from './GoogleConnection'

/** FullCalendar'da tüm gün etkinliğin bitişi hariç tutulur; son gün +1 */
const toCalendarEvent = (occurrence: ICalendarOccurrence): EventInput => {
  const end = occurrence.allDay
    ? toDateOnly(new Date(new Date(`${occurrence.end.substring(0, 10)}T00:00:00`).getTime() + 86400000))
    : occurrence.end
  return {
    id: `${occurrence.eventId}_${occurrence.occurrenceDate}`,
    title: `${occurrence.type === 'TASK' ? (occurrence.completed ? '✓ ' : '☐ ') : ''}${occurrence.title}`,
    start: occurrence.allDay ? occurrence.start.substring(0, 10) : occurrence.start,
    end,
    allDay: occurrence.allDay,
    backgroundColor: occurrence.color || '#009ef7',
    borderColor: occurrence.color || '#009ef7',
    classNames: occurrence.completed ? ['opacity-50', 'text-decoration-line-through'] : [],
    extendedProps: {occurrence},
  }
}

/** Takvim: görevler, etkinlikler (tekrarlarıyla) ve mülakatlar */
const CalendarPage: FC = () => {
  const location = useLocation()
  const calendarRef = useRef<FullCalendar>(null)
  const [range, setRange] = useState<{from: string; to: string} | null>(null)
  const [occurrences, setOccurrences] = useState<ICalendarOccurrence[]>([])
  const [selected, setSelected] = useState<ICalendarOccurrence | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<ICalendarOccurrence | null>(null)
  const [newRange, setNewRange] = useState<{start: Date; end: Date | null; allDay: boolean} | null>(null)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState<{occurrence: ICalendarOccurrence; series: boolean} | null>(null)

  const fetchOccurrences = useCallback(async (from: string, to: string) => {
    try {
      setOccurrences((await loadOccurrences(from, to)).data.payload)
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Takvim yüklenemedi'))
    }
  }, [])

  useEffect(() => {
    if (range) fetchOccurrences(range.from, range.to)
  }, [range, fetchOccurrences])

  useEffect(() => {
    if (new URLSearchParams(location.search).get('google') === 'connected') {
      showSuccessToast('Google hesabınız bağlandı; mülakatlarda Meet linki oluşturabilirsiniz')
    }
  }, [location.search])

  const reload = () => range && fetchOccurrences(range.from, range.to)

  const handleDatesSet = (arg: DatesSetArg) => {
    // FullCalendar bitişi hariç tutar; sunucu aralığı dahil kabul eder
    const lastDay = new Date(arg.end.getTime() - 86400000)
    setRange({from: toDateOnly(arg.start), to: toDateOnly(lastDay)})
  }

  const openNew = (start: Date, end: Date | null, allDay: boolean) => {
    setEditing(null)
    setNewRange({start, end, allDay})
    setFormOpen(true)
  }

  const handleSubmit = async (request: ICalendarEventRequest) => {
    setSaving(true)
    try {
      if (editing) {
        await updateCalendarEvent(editing.eventId, request)
        showSuccessToast('Güncellendi')
      } else {
        await createCalendarEvent(request)
        showSuccessToast(request.type === 'TASK' ? 'Görev eklendi' : 'Etkinlik eklendi')
      }
      setFormOpen(false)
      setEditing(null)
      reload()
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Kaydedilemedi'))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deleting) return
    const {occurrence, series} = deleting
    try {
      await deleteCalendarEvent(occurrence.eventId, series ? undefined : occurrence.occurrenceDate)
      showSuccessToast(series ? 'Silindi' : 'Bu tekrar silindi')
      setSelected(null)
      reload()
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Silinemedi'))
    } finally {
      setDeleting(null)
    }
  }

  const handleToggleCompleted = async (occurrence: ICalendarOccurrence) => {
    try {
      await setTaskCompleted(occurrence.eventId, occurrence.occurrenceDate, !occurrence.completed)
      setSelected(null)
      reload()
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Güncellenemedi'))
    }
  }

  return (
    <>
      <PageTitle breadcrumbs={[]}>Takvim</PageTitle>

      <div className='card'>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bolder fs-3 mb-1'>Takvim</span>
            <span className='text-muted mt-1 fw-bold fs-7'>
              Gün ya da saat seçerek görev / etkinlik ekleyin. Mülakatlar Aday Takip ekranından planlanır.
            </span>
          </h3>
          <div className='card-toolbar gap-3'>
            <GoogleConnectionBar />
            <button type='button' className='btn btn-sm btn-primary' onClick={() => openNew(new Date(), null, false)}>
              <i className='bi bi-plus-lg'></i>
              Yeni
            </button>
          </div>
        </div>
        <div className='card-body pt-0'>
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            locale={trLocale}
            initialView='dayGridMonth'
            headerToolbar={{left: 'prev,next today', center: 'title', right: 'dayGridMonth,timeGridWeek,timeGridDay'}}
            height='auto'
            nowIndicator
            selectable
            dayMaxEvents={4}
            events={occurrences.map(toCalendarEvent)}
            datesSet={handleDatesSet}
            select={(arg: DateSelectArg) => {
              // Ay görünümünde sürükleyerek seçimde son gün hariçtir
              const end = arg.allDay ? new Date(arg.end.getTime() - 86400000) : arg.end
              openNew(arg.start, end, arg.allDay)
            }}
            eventClick={(arg: EventClickArg) => setSelected(arg.event.extendedProps.occurrence as ICalendarOccurrence)}
          />
        </div>
      </div>

      <EventFormModal
        show={formOpen}
        editing={editing}
        initialStart={newRange?.start}
        initialEnd={newRange?.end}
        initialAllDay={newRange?.allDay}
        saving={saving}
        onClose={() => {
          setFormOpen(false)
          setEditing(null)
        }}
        onSubmit={handleSubmit}
      />

      <EventDetailModal
        occurrence={selected}
        interviewPath={HR_INTERVIEW_PATH}
        onClose={() => setSelected(null)}
        onEdit={(occurrence) => {
          setSelected(null)
          setEditing(occurrence)
          setFormOpen(true)
        }}
        onDeleteOccurrence={(occurrence) => setDeleting({occurrence, series: false})}
        onDeleteSeries={(occurrence) => setDeleting({occurrence, series: true})}
        onToggleCompleted={handleToggleCompleted}
      />

      <ConfirmModal
        show={!!deleting}
        header='Silme Onayı'
        text={
          deleting?.series
            ? deleting.occurrence.recurrenceType !== 'NONE'
              ? `"${deleting.occurrence.title}" serisinin tüm tekrarları silinecek. Devam edilsin mi?`
              : `"${deleting.occurrence.title}" silinecek. Devam edilsin mi?`
            : `"${deleting?.occurrence.title ?? ''}" yalnızca ${deleting?.occurrence.occurrenceDate} tarihli tekrarı silinecek.`
        }
        handleClose={() => setDeleting(null)}
        applyOnClick={handleDelete}
      />

      <ToastContainer position='top-right' autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick pauseOnHover />
    </>
  )
}

export {CalendarPage}
